<script setup lang="ts">
  import { ref, watch, computed, onMounted, onUnmounted } from 'vue'
  import { type TForm, type Document, FormsService } from '@/types/generated'
  import { useNewPdfStore, type PrefillData } from '@/store/newPdfStore'
  import { useSignatures } from '@/store/signatureStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { VuePDF, type AnnotationEventPayload } from '@tato30/vue-pdf'
  import type { PageViewport } from 'pdfjs-dist'
  import { useTranslation } from '@/composables/useTranslation'
  import InboxEmpty from '@/components/svg/Illustrations/InboxEmpty.vue'
  import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription
  } from '@/components/ui/dialog'
  import GenericPdfNav from '@/components/pdf/genericPdfNav.vue'
  import { Button } from '@/components/ui/button'
  import { ScrollArea } from '@/components/ui/scroll-area'
  import { Input } from '@/components/ui/input'
  import {
    Plus,
    Loader2,
    Check,
    Search,
    Pencil,
    MailCheck,
    RotateCcw,
    X,
    ChevronLeft,
    ChevronRight
  } from 'lucide-vue-next'
  import SignModal from './SignModal.vue'

  const props = defineProps<{
    show: boolean
    title?: string
    description?: string
    initialSelectedDocuments?: TForm[]
  }>()

  const emit = defineEmits<{
    (e: 'update:show', value: boolean): void
    (e: 'select', documents: TForm[]): void
  }>()

  const { t } = useTranslation({
    en: {
      attach: 'Attach',
      cancel: 'Cancel',
      page: 'Page {from} of {to}',
      preview: 'Select a document template from the list to preview',
      search: 'Search document templates...',
      sentDate: 'Sent on {date}',
      remove: 'Remove',
      title: 'Attach Documents ({docCount})',
      attached: 'Attached',
      done: 'Done',
      error: 'Error loading document',
      loading: 'Loading...',
      loadingDocument: 'Loading document...',
      reset: 'Reset',
      previous: 'Previous',
      next: 'Next',
      noDocuments: 'No documents found',
      loadingDocuments: 'Loading documents...'
    },
    de: {
      attach: 'Anhängen',
      cancel: 'Abbrechen',
      page: 'Seite {from} von {to}',
      preview: 'Wählen Sie eine Dokumentenvorlage aus der Liste aus, um eine Vorschau anzuzeigen',
      remove: 'Entfernen',
      search: 'Dokumentenvorlagen durchsuchen...',
      sentDate: 'Am {date} gesendet',
      title: 'Dokumente anhängen ({docCount})',
      attached: 'Angehängt',
      done: 'Fertig',
      error: 'Dokument konnte nicht geladen werden',
      loading: 'Laden...',
      loadingDocument: 'Dokument wird geladen...',
      reset: 'Zurücksetzen',
      previous: 'Zurück',
      next: 'Weiter',
      noDocuments: 'Keine Dokumente gefunden',
      loadingDocuments: 'Dokumente werden geladen...'
    }
  })

  // Store
  const pdfStore = useNewPdfStore()
  const signatureStore = useSignatures()
  const caseStore = useCaseStore()

  // State
  const previewingDocument = ref<TForm | null>(null)
  const selectedDocuments = ref<TForm[]>([])
  const resettingForm = ref(false)
  const search = ref('')

  // Pagination state
  const currentPage = ref(1)
  const totalPages = ref(1)
  const totalCount = ref(0)
  const documents = ref<TForm[]>([])
  const isLoadingDocuments = ref(false)
  const searchDebounceTimeout = ref<ReturnType<typeof setTimeout> | null>(null)
  const pageSize = 20

  watch(
    () => props.initialSelectedDocuments,
    (newDocs) => {
      if (newDocs) {
        // Important: Create a fresh array instead of reference
        selectedDocuments.value = JSON.parse(JSON.stringify(newDocs))
      } else {
        selectedDocuments.value = []
      }
    },
    { immediate: true }
  )

  // Computed
  const caseRecord = computed(() => caseStore.caseRecord)
  const pdfState = computed(() => pdfStore.selectedPdfs.get(previewingDocument.value?.id || ''))
  const pdfStateAnnotationMap = computed(() => {
    const docId = previewingDocument.value?.id || ''
    return pdfStore.getAnnotationsMap(docId)
  })

  const filteredDocuments = computed(() => {
    return documents.value
  })

  const prefilledData = computed<PrefillData>(() => {
    return {
      client_first_name: caseRecord.value?.data?.client.first_name,
      client_last_name: caseRecord.value?.data?.client.last_name,
      client_email: caseRecord.value?.data?.client.email,
      client_phone: caseRecord.value?.data?.client.phone,
      probably_client_city: caseRecord.value?.data?.client.city,
      probably_client_zip: caseRecord.value?.data?.client.zip,
      probably_client_street: caseRecord.value?.data?.client.street,
      probably_client_street_number: caseRecord.value?.data?.client.street_number
    }
  })

  // Methods
  const fetchDocuments = async (page: number = 1, searchQuery?: string) => {
    isLoadingDocuments.value = true
    try {
      const offset = (page - 1) * pageSize
      const response = await FormsService.formsList({
        limit: pageSize,
        offset,
        search: searchQuery,
        tag: 'MASTER'
      })

      let fetchedDocuments = response.results || []

      // Only include selected documents when there's no active search
      // This ensures search results are clean and only show documents matching the search
      if (
        !searchQuery &&
        props.initialSelectedDocuments &&
        props.initialSelectedDocuments.length > 0
      ) {
        const fetchedIds = new Set(fetchedDocuments.map((doc) => doc.id))
        const missingSelectedIds = props.initialSelectedDocuments
          .filter((doc) => !fetchedIds.has(doc.id))
          .map((doc) => doc.id)

        // Fetch any missing selected documents individually
        if (missingSelectedIds.length > 0) {
          try {
            const missingDocsPromises = missingSelectedIds.map(async (docId) => {
              try {
                const docResponse = await FormsService.formsRetrieve({ id: docId })
                return docResponse
              } catch (error) {
                console.error(`Error fetching document ${docId}:`, error)
                return null
              }
            })

            const missingDocsResults = await Promise.all(missingDocsPromises)
            const missingDocs = missingDocsResults.filter((doc) => doc !== null) as TForm[]

            // Add missing documents to the fetched documents
            fetchedDocuments = [...fetchedDocuments, ...missingDocs]
          } catch (error) {
            console.error('Error fetching missing selected documents:', error)
          }
        }
      }

      documents.value = fetchedDocuments
      totalCount.value = response.count || 0
      totalPages.value = Math.ceil(totalCount.value / pageSize)
      currentPage.value = page
    } catch (error) {
      console.error('Error fetching documents:', error)
      documents.value = []
      totalCount.value = 0
      totalPages.value = 1
    } finally {
      isLoadingDocuments.value = false
    }
  }

  const handleSearch = (event: Event) => {
    const value = (event.target as HTMLInputElement).value
    search.value = value

    if (searchDebounceTimeout.value) {
      clearTimeout(searchDebounceTimeout.value)
    }

    searchDebounceTimeout.value = setTimeout(async () => {
      currentPage.value = 1
      await fetchDocuments(1, value || undefined)
    }, 300)
  }

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages.value && page !== currentPage.value) {
      fetchDocuments(page, search.value || undefined)
    }
  }

  const getDocumentSentDate = (doc: TForm): string | null => {
    // Check if document was previously sent
    if (pdfStore.getSentDate(doc.id)) {
      return pdfStore.getSentDate(doc.id)
    }

    // Check all documents in the case
    const sentDocuments =
      caseRecord.value?.documents.filter(
        (doc: Document) => doc.document_type === 'Dokumentenvorlage'
      ) || []
    const currentDocument = sentDocuments.find((sentDoc) => sentDoc.title === doc.document_name)
    if (currentDocument) {
      const created_at = new Intl.DateTimeFormat('de-DE').format(
        new Date(currentDocument.created_at)
      )
      pdfStore.setSentDate(doc.id, created_at)
      return created_at
    }
    return null
  }

  const formatDocumentName = (doc: any): string => {
    if (doc.title) {
      return doc.title
    }
    if (doc.document_name) {
      return doc.document_name.replace(/^forms\/org\/[^/]+\//, '')
    }
    return ''
  }

  const isDocumentSelected = (docId: string) => {
    return selectedDocuments.value.some((d) => d.id === docId)
  }

  const handleDocumentPreview = async (doc: TForm) => {
    previewingDocument.value = doc
    signatureStore.setPdfStore('new')
    try {
      await pdfStore.loadPdf(doc)
    } catch (error) {
      console.error('Error loading document:', error)
    }
  }

  const emitSelected = () => {
    emit('select', selectedDocuments.value)
  }

  const handleDocumentAdd = (doc: TForm) => {
    if (!doc) return
    if (!isDocumentSelected(doc.id)) {
      selectedDocuments.value = [...selectedDocuments.value, doc]
      emit('select', selectedDocuments.value)
    }
  }

  const handleDocumentRemove = (doc: TForm) => {
    selectedDocuments.value = selectedDocuments.value.filter((d) => d.id !== doc.id)
    emit('select', selectedDocuments.value)
  }

  const handleSubmit = () => {
    emitSelected()
    emit('update:show', false)
  }

  const setPage = (page: number) => {
    if (previewingDocument.value && pdfStore.selectedPdfs.get(previewingDocument.value.id)) {
      pdfStore.setCurrentPage(previewingDocument.value.id, page)
    }
  }

  const onPdfLoaded = async (e: PageViewport) => {
    const pdf = await pdfState.value?.loadingTask?.promise
    if (pdf) {
      pdfStore.onPdfLoaded(previewingDocument.value?.id || '', pdf.numPages)
      if (!pdfState.value?.currentPage || pdfState.value.currentPage < 1) {
        pdfStore.setCurrentPage(previewingDocument.value?.id || '', 1)
      }
    }
  }

  const onAnnotation = (payload: AnnotationEventPayload) => {
    // Handle link annotations by opening in new tab
    if (payload.type === 'link') {
      if (payload.data.url) {
        window.open(payload.data.url, '_blank')
      }
      return
    }
    const { fieldName, value } = payload.data
    pdfStore.updateAnnotation(previewingDocument.value?.id || '', fieldName, value)
    pdfStore.setManuallyEdited(previewingDocument.value?.id || '', true)
  }

  const resetAnnotation = (doc: TForm) => {
    if (!doc) return
    resettingForm.value = true
    pdfStore.resetAnnotations(doc.id)
    resettingForm.value = false
  }

  // Watch for changes to initialSelectedDocuments
  watch(
    () => props.initialSelectedDocuments,
    (newDocs) => {
      if (newDocs) {
        selectedDocuments.value = [...newDocs]
      }
    }
  )

  // Watch for dialog open/close
  watch(
    () => props.show,
    (show) => {
      if (!show) {
        if (searchDebounceTimeout.value) {
          clearTimeout(searchDebounceTimeout.value)
          searchDebounceTimeout.value = null
        }
        previewingDocument.value = null
        search.value = ''
        currentPage.value = 1
      } else if (props.initialSelectedDocuments) {
        selectedDocuments.value = [...props.initialSelectedDocuments]
      }

      if (show) {
        fetchDocuments(1)
      }
    }
  )

  onMounted(() => {
    if (prefilledData.value) {
      pdfStore.setPrefillData(prefilledData.value)
    }
  })

  onUnmounted(() => {
    if (searchDebounceTimeout.value) {
      clearTimeout(searchDebounceTimeout.value)
      searchDebounceTimeout.value = null
    }
    signatureStore.cleanup()
  })
</script>

<template>
  <Dialog
    :open="show"
    :modal="true"
    @update:open="(v) => emit('update:show', v)"
  >
    <DialogContent class="max-w-6xl p-6">
      <div
        class="flex h-[80vh] w-full flex-col"
        role="dialog"
        aria-modal="true"
      >
        <div>
          <DialogHeader>
            <DialogTitle>
              {{ props.title || t('title', { docCount: totalCount }) }}
            </DialogTitle>
            <DialogDescription>
              {{ props.description }}
            </DialogDescription>
          </DialogHeader>
        </div>

        <div class="flex h-[70vh] flex-1 gap-3 pt-3">
          <!-- Left side: Document list -->
          <div class="flex w-1/3 flex-col">
            <!-- Search input -->
            <div class="relative mb-3">
              <Search class="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
              <Input
                :model-value="search"
                :placeholder="t('search')"
                class="w-full border border-gray-200 pl-9 shadow-sm focus-visible:ring-2 focus-visible:ring-purple-500"
                @input="handleSearch"
              />
            </div>

            <!-- Document list -->
            <ScrollArea
              class="flex-1 rounded-lg border p-2"
              data-cy="documentsList"
            >
              <div
                v-if="isLoadingDocuments"
                class="flex items-center justify-center py-8"
              >
                <Loader2 class="h-6 w-6 animate-spin text-gray-500" />
                <span class="ml-2 text-sm text-gray-500">{{ t('loadingDocuments') }}</span>
              </div>
              <div
                v-else-if="filteredDocuments.length === 0"
                class="flex h-full flex-col items-center justify-center py-8"
              >
                <p class="mt-2 text-center text-gray-500">
                  {{ t('noDocuments') }}
                </p>
              </div>
              <div
                v-else
                class="divide-y"
              >
                <div
                  v-for="doc in filteredDocuments"
                  :key="doc.id"
                  class="group flex items-center gap-3 border-l-4 p-3 transition-colors hover:bg-gray-50"
                  :class="{
                    'bg-gray-50': previewingDocument?.id === doc.id && !isDocumentSelected(doc.id),
                    'border-l-purple-600': isDocumentSelected(doc.id),
                    'border-l-transparent': !isDocumentSelected(doc.id)
                  }"
                  data-cy="documentItem"
                >
                  <!-- Checkbox section - only handles selection/deselection -->
                  <div
                    class="flex h-5 w-5 items-center justify-center rounded border border-gray-300 transition-colors"
                    :class="{
                      'border-purple-600 bg-purple-600': isDocumentSelected(doc.id)
                    }"
                    data-cy="documentItemCheckbox"
                    @click="
                      isDocumentSelected(doc.id)
                        ? handleDocumentRemove(doc)
                        : handleDocumentAdd(doc)
                    "
                  >
                    <Check
                      v-if="isDocumentSelected(doc.id)"
                      class="h-3.5 w-3.5 text-white"
                    />
                  </div>

                  <!-- Document info section - only handles preview -->
                  <div
                    class="min-w-0 flex-1 cursor-pointer"
                    @click="handleDocumentPreview(doc)"
                  >
                    <div class="flex items-center gap-2">
                      <span
                        class="block truncate"
                        data-cy="documentItemName"
                      >
                        {{ formatDocumentName(doc) }}
                      </span>
                      <div class="flex gap-1">
                        <div
                          v-if="pdfStore.getManuallyEdited(doc.id)"
                          class="group relative"
                        >
                          <Pencil class="h-3.5 w-3.5 text-gray-500" />
                        </div>
                        <div
                          v-if="getDocumentSentDate(doc)"
                          class="flex items-center gap-1 text-xs text-gray-500"
                        >
                          <MailCheck class="h-4 w-4 text-gray-500" />
                          {{ getDocumentSentDate(doc) }}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollArea>
            <div
              v-if="totalPages > 1"
              class="mt-4 flex items-center justify-start gap-2"
            >
              <Button
                variant="secondary"
                size="sm"
                :disabled="currentPage === 1"
                @click="goToPage(currentPage - 1)"
              >
                <ChevronLeft class="h-4 w-4" />
              </Button>
              <span class="text-sm text-gray-700">
                {{ t('page', { from: currentPage, to: totalPages }) }}
              </span>
              <Button
                variant="secondary"
                size="sm"
                :disabled="currentPage === totalPages"
                @click="goToPage(currentPage + 1)"
              >
                <ChevronRight class="h-4 w-4" />
              </Button>
            </div>
          </div>

          <!-- Right side: PDF preview -->
          <div class="flex w-2/3 flex-col rounded-lg border border-gray-200 shadow-sm">
            <div
              v-if="previewingDocument"
              class="flex h-full flex-col p-3"
            >
              <!-- Document header with title and sent date -->
              <div class="mb-3 grid w-full grid-cols-3 items-center">
                <h3 class="col-span-2 truncate p-3 text-gray-900">
                  {{ previewingDocument.title || (previewingDocument as any).document_name }}
                </h3>
                <div
                  v-if="getDocumentSentDate(previewingDocument)"
                  class="flex items-center gap-1 justify-self-end text-sm text-gray-500"
                >
                  <MailCheck class="h-3.5 w-3.5 text-gray-500" />
                  {{ t('sentDate', { date: getDocumentSentDate(previewingDocument) }) }}
                </div>
              </div>

              <!-- PDF viewer container -->
              <div class="w-full flex-1 overflow-auto rounded-lg border border-gray-200">
                <!-- PDF Display -->
                <div class="relative h-full flex-1">
                  <div
                    v-if="pdfState?.isLoading"
                    class="absolute inset-0 flex h-full items-center justify-center bg-white bg-opacity-80"
                  >
                    <div class="text-gray-500">{{ t('loadingDocument') }}</div>
                  </div>
                  <div
                    v-else-if="pdfState?.error"
                    class="absolute inset-0 flex h-full items-center justify-center"
                  >
                    <div class="text-red-500">{{ t('error') }}</div>
                  </div>
                  <div v-if="pdfState && !pdfState.isLoading && !pdfState.error">
                    <div class="flex h-full items-center justify-center">
                      <div class="pdf-container">
                        <VuePDF
                          ref="vuePDFRef"
                          :key="`pdf-${previewingDocument?.id}`"
                          class="pdf"
                          on-layer
                          text-layer
                          annotation-layer
                          :annotations-map="pdfStateAnnotationMap"
                          :scale="1.2"
                          :pdf="pdfState?.loadingTask"
                          :page="pdfState?.currentPage || 1"
                          @loaded="onPdfLoaded"
                          @annotation="onAnnotation"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="mt-3 flex items-center justify-between">
                <!-- Navigation controls -->
                <GenericPdfNav
                  :current-page="pdfState?.currentPage || 1"
                  :total-pages="pdfState?.pages || 1"
                  @update-page="setPage"
                />

                <div class="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    class="gap-2 border border-gray-200 shadow-sm"
                    @click="resetAnnotation(previewingDocument)"
                  >
                    <RotateCcw class="h-4 w-4" />
                    <span>{{ t('reset') }}</span>
                  </Button>
                  <Button
                    v-if="previewingDocument && isDocumentSelected(previewingDocument.id)"
                    variant="ghost"
                    size="sm"
                    class="gap-2 border border-gray-200 text-red-700 shadow-sm"
                    :disabled="!previewingDocument"
                    @click="handleDocumentRemove(previewingDocument)"
                  >
                    <X class="h-4 w-4" />
                    <span>{{ t('remove') }}</span>
                  </Button>
                  <Button
                    v-else
                    variant="ghost"
                    size="sm"
                    class="gap-2 border border-gray-200 shadow-sm"
                    :disabled="!previewingDocument"
                    data-cy="attachBtn"
                    @click="handleDocumentAdd(previewingDocument)"
                  >
                    <Plus class="h-4 w-4" />
                    <span>{{ t('attach') }}</span>
                  </Button>
                </div>
              </div>
            </div>
            <div
              v-else
              class="flex h-full w-full flex-col items-center justify-center overflow-hidden rounded-lg border
                border-gray-200 p-16 font-medium shadow-sm"
            >
              {{ t('preview') }}
              <div style="width: 75%; height: 75%">
                <InboxEmpty />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer with action buttons -->
        <div class="mt-4 flex items-center justify-between border-t pt-4">
          <div class="text-sm text-gray-500">
            {{
              selectedDocuments.length > 0
                ? `${selectedDocuments.length} ${selectedDocuments.length === 1 ? 'Dokument' : 'Dokumente'} angehängt`
                : 'Keine Dokumente angehängt'
            }}
          </div>
          <div class="flex gap-2">
            <Button
              variant="default"
              class="bg-purple-600 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:opacity-50"
              size="sm"
              :disabled="selectedDocuments.length === 0"
              data-cy="attachBtn"
              @click="handleSubmit"
            >
              {{ t('done') }}
            </Button>
          </div>
        </div>
      </div>
      <SignModal v-if="signatureStore.showSignModal" />
    </DialogContent>
  </Dialog>
</template>

<style scoped>
  .pdf-container {
    background: #f5f5f5;
    height: 100%;
    min-height: 400px;
    width: 100%;
    max-width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .pdf {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .pdf :deep(canvas),
  .pdf :deep(.textLayer),
  .pdf :deep(.annotationLayer) {
    max-width: 100% !important;
    max-height: 100% !important;
    object-fit: contain;
    width: auto !important;
    height: auto !important;
  }

  .pdf :deep(.linkAnnotation) {
    cursor: pointer;
  }
</style>
